<template>
  <div class="flex gap-4 items-center flex-shrink-1 flex-grow-0">
    <VTooltip
      v-if="isCompleteConsultationButtonVisible"
      :disabled="!consultation.hasActiveCall"
      location="top center"
    >
      {{ t('Consultation cannot be completed until everyone has left the call.') }}
      <template #activator="{ props: tooltipProps }">
        <span v-bind="tooltipProps">
          <VBtn
            :disabled="!isStatusReportsCompleted || consultation.hasActiveCall"
            class="button-large"
            variant="flat"
            color="primary"
            size="large"
            data-testid="complete-consultation-btn"
            @click.stop.prevent="handleComplete"
            >{{ t('Complete consultation') }}</VBtn
          >
        </span>
      </template>
    </VTooltip>
    <VBtn
      :class="`button-large ${isStatusInProgress ? 'border-[#0000000D] bg-[#0000000D]' : ''}`"
      :color="isStatusInProgress ? '' : 'primary'"
      :variant="isStatusInProgress ? 'outlined' : 'flat'"
      size="large"
      v-if="isCreateReportButtonVisible"
      data-testid="create-report-btn"
      @click.stop.prevent="handleCreateReport"
      >{{ t('Create consultation report') }}
    </VBtn>

    <VBtn
      class="button-large border-[#0000001A]"
      variant="outlined"
      size="large"
      prependIcon="close"
      v-if="isHideButtonVisible"
      data-testid="hide-btn"
      @click.stop.prevent="handleHide"
      >{{ t('Hide') }}
    </VBtn>

    <VBtn
      icon="custom:deleteIcon"
      class="button-small"
      variant="plain"
      size="40px"
      v-if="isDeleteButtonVisible"
      :disabled="isStatusInProgress"
      data-testid="delete-consultation-btn"
      @click.stop.prevent="handleDelete"
    />

    <VBtn
      class="button-large border-[#0000001A]"
      size="large"
      variant="outlined"
      v-if="isDeclineButtonVisible"
      data-testid="decline-consultation-btn"
      @click.stop.prevent="handleDecline"
      >{{ t('Decline') }}
    </VBtn>

    <VBtn
      class="button-large"
      color="primary"
      variant="flat"
      size="large"
      v-if="isAcceptConsultationButtonVisible"
      data-testid="accept-consultation-btn"
      @click.stop.prevent="handleAccept"
      >{{ t('Accept consultation') }}
    </VBtn>

    <VBtn
      class="button-large"
      color="primary"
      variant="flat"
      size="large"
      v-if="isJoinNowButtonVisible && !isUserInCall"
      data-testid="join-now-btn"
      @click.stop.prevent="handleJoin"
      prependIcon="custom:phone"
      >{{ t('Join video call') }}
    </VBtn>

    <VBtn
      v-if="isExportButtonVisible"
      class="button-large border-[#0000001A]"
      size="large"
      variant="outlined"
      data-testid="export-appointment-btn"
      @click.stop.prevent="handleExport"
      >{{ t('Export appointment') }}
    </VBtn>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useScheduledConsultationRequest } from '@/composables/useScheduledConsultationRequest'
import type { ScheduledConsultationDTO } from '@/generated'
import { routeNames } from '@/router/routeNames'
import { useConsultationRequestStore } from '@/stores/consultationRequest'
import { NoticeType } from '@/stores/models/Notice'
import { useNoticeStore } from '@/stores/notice'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { VBtn, VTooltip } from 'vuetify/components'
import { useRepeatWhileMounted } from '@/composables/repeatWhileMounted'
import { downloadConsultationICal } from '@/util/calendarExport'
import { translateScheduledConsultationTypeDTO } from '@/util/translations'

const { t } = useI18n()
const router = useRouter()

const noticeStore = useNoticeStore()

const { consultation } = defineProps<{
  consultation: ScheduledConsultationDTO
}>()

async function handleComplete() {
  await completeScheduledConsultationById(consultation.id)
}

async function handleAccept() {
  await acceptScheduledConsultationById(consultation.id)
}

async function handleDecline() {
  await declineScheduledConsultationById(consultation.id)
}

async function handleJoin() {
  router.push({ name: routeNames.SCHEDULED_CALL, params: { consultationId: consultation.id } })
}

async function handleCreateReport() {
  router.push({
    name: routeNames.CONSULTATION_DETAILS,
    params: { consultationId: consultation.id }
  })
}
async function handleHide() {
  await hideScheduledConsultationById(consultation.id)
}

async function handleDelete() {
  await deleteOutgoingScheduledConsultationById(consultation.id).then(() => {
    noticeStore.add({
      type: NoticeType.SUCCESS,
      message: t('Consultation deleted successfully'),
      messageParameters: { id: consultation.id }
    })
  })
  router.push({
    name: routeNames.CONSULTATIONS
  })
}

const {
  deleteOutgoingScheduledConsultationById,
  completeScheduledConsultationById,
  hideScheduledConsultationById
} = useConsultationRequestStore()

const {
  isFinishedConsultation,
  isOutgoingRequest,
  isIncomingRequest,
  isStatusInProgress,
  isStatusReportsCompleted,
  isStatusReportsPending,
  isStatusCreated,
  isStatusDeleted,
  isParticipationPending,
  isParticipationAccepted,
  hasUserJoinedConsultation,
  hasCurrentUserFinalizedReport,
  isStatusCompleted,
  acceptScheduledConsultationById,
  declineScheduledConsultationById,
  setCurrentTime
} = useScheduledConsultationRequest(consultation)

useRepeatWhileMounted(() => {
  setCurrentTime()
}, 15000)

const isCompleteConsultationButtonVisible = computed(() => {
  return isOutgoingRequest.value && (isStatusReportsPending.value || isStatusReportsCompleted.value)
})

const isCreateReportButtonVisible = computed(() => {
  const isReportAllowedStatus =
    isStatusInProgress.value || isStatusReportsPending.value || isStatusReportsCompleted.value

  const isUserAllowedToWriteReport =
    isOutgoingRequest.value || isParticipationAccepted.value || hasUserJoinedConsultation.value

  return isReportAllowedStatus && isUserAllowedToWriteReport && !hasCurrentUserFinalizedReport.value
})

const isHideButtonVisible = computed(() => {
  if (isOutgoingRequest.value) {
    return isStatusCompleted.value
  } else {
    return isStatusCompleted.value || isStatusReportsCompleted.value || isStatusDeleted.value
  }
})

const isDeleteButtonVisible = computed(() => {
  return isOutgoingRequest.value && isStatusCreated.value
})

const isDeclineButtonVisible = computed(() => {
  return (
    isIncomingRequest.value &&
    isParticipationPending.value &&
    (isStatusCreated.value || isStatusInProgress.value)
  )
})

const isAcceptConsultationButtonVisible = computed(() => {
  return isIncomingRequest.value && isParticipationPending.value && isStatusCreated.value
})

const isJoinNowButtonVisible = computed(() => {
  return (isOutgoingRequest.value || isIncomingRequest.value) && !isFinishedConsultation.value
})

const isUserInCall = computed(() => consultation.isUserInCall)

const isExportButtonVisible = computed(() => {
  if (isOutgoingRequest.value) {
    return true
  }
  return isIncomingRequest.value && !isParticipationPending.value
})

const handleExport = () => {
  const translatedType = translateScheduledConsultationTypeDTO(consultation.type, t)
  downloadConsultationICal(consultation, translatedType, t)
}
</script>
